import React from 'react'
import { motion } from 'framer-motion'
import { 
  BookOpen, 
  Camera, 
  Mountain, 
  Code, 
  Users,
  Target
} from 'lucide-react'

const Interests: React.FC = () => {
  const interests = [
    {
      icon: <Code className="w-8 h-8" />,
      title: "AI & Emerging Technologies",
      description: "I'm passionate about working at the intersection of AI and business applications. Recent work includes experimenting with LLMs for knowledge agents, OCR + LLM for ID verification, ElevenLabs TTS + Ready Player Me avatars, and ML models trained on MySQL datasets.",
      emoji: "🧠",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Continuous Learning",
      description: "My curiosity pushes me to test new SDKs, automation workflows, and creative integrations. I often prototype ideas myself before scaling them with my teams, staying current with emerging domains like AI/ML, Blockchain, Computer Vision, and 3D/AR/VR.",
      emoji: "📚",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Mentoring & Team Development",
      description: "I've mentored 10+ project managers, standardizing PM best practices and boosting project success rates. I enjoy building cross-functional teams and setting up efficient delivery processes that enable others to succeed.",
      emoji: "🎤",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Strategic Business Growth",
      description: "Currently preparing to expand my leadership role, combining experience in sales strategy, project delivery, and technology to drive business growth while continuing to explore cutting-edge tools and AI-driven solutions.",
      emoji: "🚀",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: <Camera className="w-8 h-8" />,
      title: "Health & Wellness",
      description: "I'm in my 40s and currently focusing on health, fitness, and weight management, applying the same discipline and experimentation I use in tech to wellness. I'm curious about supplements, lifestyle optimization, and habit-building.",
      emoji: "💪",
      color: "from-yellow-500 to-orange-500"
    },
    {
      icon: <Mountain className="w-8 h-8" />,
      title: "Gardening & Growth",
      description: "I have a strong passion for gardening, whether it's nurturing a money plant, propagating dragon fruit, or simply enjoying the patience it teaches. To me, growth—whether in people, technology, or plants—is about patience, consistency, and care.",
      emoji: "🌱",
      color: "from-amber-500 to-yellow-500"
    }
  ]

  const currentFocus = [
    { name: "Building scalable MERN/Laravel/Swift/React Native apps", icon: "📱", description: "Full-stack mobile and web development" },
    { name: "Implementing AI agents & private LLMs", icon: "🤖", description: "AI-driven customer support and insights" },
    { name: "Optimizing databases and APIs", icon: "⚡", description: "Performance-heavy use cases" },
    { name: "Enhancing sales and business development", icon: "📈", description: "Software services growth" },
    { name: "3D/AR/VR + AI integrations", icon: "🥽", description: "Immersive technology experiences" },
    { name: "Health and fitness optimization", icon: "🏃", description: "Personal wellness journey" }
  ]

  const currentLearning = [
    { topic: "Advanced AI/ML Integration", progress: 85, emoji: "🧠" },
    { topic: "Blockchain Development", progress: 75, emoji: "⛓️" },
    { topic: "3D/AR/VR Technologies", progress: 70, emoji: "🥽" },
    { topic: "Emerging SDKs & APIs", progress: 80, emoji: "🔌" }
  ]

  return (
    <section id="interests" className="section-padding">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Personal <span className="gradient-text">Interests</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Beyond technology leadership, here's what drives me, inspires me, and keeps me balanced
          </p>
        </motion.div>

        {/* Main Interests */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {interests.map((interest, index) => (
            <motion.div
              key={interest.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${interest.color} rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                    {interest.icon}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 flex-1 leading-tight text-left">
                    {interest.title}
                  </h3>
                </div>
                
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-lg">
                  {interest.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Current Focus Areas */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-8">
            Current <span className="gradient-text">Focus Areas</span>
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentFocus.map((focus, index) => (
              <motion.div
                key={focus.name}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="text-4xl mb-4 flex justify-center">
                  {focus.icon}
                </div>
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  {focus.name}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {focus.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Current Learning */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-8">
            Currently <span className="gradient-text">Learning</span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {currentLearning.map((item, index) => (
              <motion.div
                key={item.topic}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="text-4xl mb-4 flex justify-center">
                  {item.emoji}
                </div>
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 text-center">
                  {item.topic}
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                    <span>Progress</span>
                    <span>{item.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${item.progress}%` }}
                      transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Personal Philosophy */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-8 lg:p-12 text-white">
            <div className="text-6xl mb-6">🌟</div>
            <h3 className="text-2xl lg:text-3xl font-bold mb-6">
              Life Philosophy
            </h3>
            <p className="text-lg lg:text-xl opacity-90 max-w-3xl mx-auto leading-relaxed">
              "I'm a strategic leader, hands-on technologist, and curious learner who thrives in solving complex problems with clarity and precision. 
              I bring a balance of vision, execution, and empathy, whether it's for clients, teams, or personal passions. 
              Above all, I strive to be practical, helpful, and honest in everything I do."
            </p>
            <div className="mt-6 text-lg opacity-90">
              — Rishi Malhotra
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Interests
